import React, { useRef, useEffect, useState, useContext } from "react";
import {
    Card,
    Row,
    Col,
    Typography,
    Table,
    Divider,
    Button,
    Image,
    Select,
    message,
    Spin,
} from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import BashyamLogo from "../assests/baashyaam_logo.png";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const { Title, Text } = Typography;
const { Option } = Select;

// Colors lifted directly from the BFMS_BULKPAYSLIP.xlsm "PAYSLIP" sheet
const HEADER_BAND_COLOR = "#0e497b";   // A4 fill — "PAY SLIP FOR THE MONTH OF" band
const SECTION_HEADER_COLOR = "#0e497b"; // A13/D13/B14/C14/A29 fill — section header bands

const PaySlip = ({ employeeId, initialMonth, initialYear, onBack }) => {
    const slipRef = useRef();
    const cardRef = useRef();
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState(null);
    const [month, setMonth] = useState(initialMonth || new Date().getMonth() + 1);
    const [year, setYear] = useState(initialYear || new Date().getFullYear());
    const [loading, setLoading] = useState(false);
    const [downloading, setDownloading] = useState(false);

    // 🔹 Fetch payslip data
    const fetchData = async () => {
        try {
            setLoading(true);

            // When an employeeId is provided (e.g. HR viewing someone else's
            // payslip from the employee list), scope the request to that
            // employee. Otherwise this falls back to the logged-in user's
            // own payslip, same as before.
            const url = employeeId
                ? `${backUrl}/api/payroll/preview/payslip/${year}/${month}/?employee_id=${employeeId}`
                : `${backUrl}/api/payroll/preview/payslip/${year}/${month}/`;

            const res = await axios.get(url, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`,
                },
            });
            setData(res.data);
            console.log("Payslip Data:", res.data);
        } catch (error) {
            console.error("Error fetching payslip:", error);

            if (error.response?.data?.detail) {
                message.error(error.response.data.detail);
            } else {
                message.error("Failed to fetch payslip data");
            }

            setData(null);
        } finally {
            setLoading(false);
        }
    };

    console.log("Authdata =>", authData)

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, year, employeeId]);

    // 🔹 Client-side PDF generation — captures the rendered payslip Card
    // (cardRef, not the padded grey wrapper around it) as a canvas and
    // drops it into a SINGLE A4 jsPDF page, instead of hitting a backend
    // PDF-rendering endpoint. What you see on screen is exactly what gets
    // downloaded, scaled to fit one page rather than spilling a few rows
    // onto a near-empty second page.
    const handleDownload = async () => {
        if (!cardRef.current) return;

        setDownloading(true);
        try {
            const node = cardRef.current;

            // Render at higher resolution for crisp text/logo in the PDF.
            const canvas = await html2canvas(node, {
                scale: 2,
                useCORS: true,
                backgroundColor: "#ffffff",
                logging: false,
                windowWidth: node.scrollWidth,
                windowHeight: node.scrollHeight,
            });

            const imgData = canvas.toDataURL("image/png");

            // A4 in mm
            const pdf = new jsPDF({
                orientation: "portrait",
                unit: "mm",
                format: "a4",
            });

            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();

            // Fit the whole payslip onto one page: scale by whichever
            // dimension (width or height) is the tighter constraint, then
            // center the result on the page. No pagination — a payslip is
            // one page, full stop.
            const widthRatio = pageWidth / canvas.width;
            const heightRatio = pageHeight / canvas.height;
            const ratio = Math.min(widthRatio, heightRatio);

            const imgWidth = canvas.width * ratio;
            const imgHeight = canvas.height * ratio;
            const xOffset = (pageWidth - imgWidth) / 2;
            const yOffset = (pageHeight - imgHeight) / 2;

            pdf.addImage(imgData, "PNG", xOffset, yOffset, imgWidth, imgHeight);

            const fileName = `PaySlip_${data?.employee?.first_name || "Employee"}_${monthOptions[month - 1]}_${year}.pdf`;
            pdf.save(fileName);

            message.success("Payslip downloaded successfully!");
        } catch (err) {
            console.error("Download failed", err);
            message.error("Failed to generate payslip PDF. Please try again.");
        } finally {
            setDownloading(false);
        }
    };


    const monthOptions = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ];

    const yearOptions = [];
    for (let y = new Date().getFullYear() - 2; y <= new Date().getFullYear() + 1; y++) {
        yearOptions.push(y);
    }

    // Section-header cell style — matches A13/D13/B14/C14/A29 fill+font in the sheet
    const sectionHeaderStyle = {
        backgroundColor: SECTION_HEADER_COLOR,
        color: "white",
        fontWeight: "bold",
        fontSize: "12px",
        padding: "6px",
        textAlign: "center",
    };

    const fs = data?.final_statement || {};
    const fmt = (v) => {
        if (v === null || v === undefined || v === "") {
            return "";
        }
        return parseFloat(v).toLocaleString();
    };

    // ── Earnings — matches PAYSLIP sheet rows A15:C22, with BOTH
    // "Fixed Earnings" (col B) and "Actual Earnings" (col C) columns ──
    const earningsRows = [
        { key: 1, name: "Basic", fixed: fs.basic_pm, actual: fs.basic_earned },
        { key: 2, name: "House Rent Allowance", fixed: fs.hra_pm, actual: fs.hra_earned },
        { key: 3, name: "Conveyance Allowance", fixed: fs.conveyance_pm, actual: fs.conveyance_earned },
        { key: 4, name: "Special Allowance", fixed: fs.special_allowance_pm, actual: fs.special_allowance_earned },
        { key: 5, name: "Gross Salary", fixed: fs.gross_salary, actual: fs.gross_earned },
        // "Other Allowance" row (sheet B20/C20) has no fixed/actual split in
        // our data model — the payload only carries a single value.
        { key: 6, name: "Other Allowance", fixed: fs.fixed_other_allowance, actual: fs.actual_other_allowance },
        { key: 7, name: "Overtime (OT) Pay", fixed: fs.ot_pay_pm, actual: fs.ot_pay },
        { key: 8, name: "Arrears", fixed: fs.arrears_pm, actual: fs.arrears },
        { key: 9, name: " ", fixed: null, actual: null },
        {
            key: 10,
            name: "Total",
            fixed: fs.fixed_earning,
            actual: fs.actual_earning,
            className: "total-row",
        },
    ];

    // ── Deductions — matches PAYSLIP sheet rows D15:E23 exactly, in order.
    // "Late" (D19/E19) was missing entirely before — added back. ──
    const deductionsRows = [
        { key: 1, name: "Flexible Allowance", amount: fs.flexible_benefit_allowance },
        { key: 2, name: "Professional Tax (PT)", amount: fs.pt_pm },
        { key: 3, name: "TDS/IT", amount: fs.tds },
        { key: 4, name: "Loss of Pay (LOP)", amount: fs.lop_deduction },
        { key: 5, name: "Late", amount: fs.late_deduction },
        { key: 6, name: "Salary/Loan Advance", amount: fs.salary_advance },
        { key: 7, name: "PF", amount: fs.pf_employee },
        { key: 8, name: "ESIC", amount: fs.esi_employee },
        { key: 9, name: "Other", amount: fs.other_deductions },
        {
            key: 10,
            name: "Total Deduction",
            amount: fs.total_deductions,
            className: "total-row",
        },
    ];

    // ── Employee details — matches PAYSLIP sheet rows A5:E12 exactly
    // (8 rows; previous version only had 6 and was missing IFSC Code /
    // Aadhar Number entirely, and had Bank Branch mis-paired). ──
    const employeeDetailRows = [
        {
            key: 1,
            col1: <Text strong>Employee Name </Text>,
            col2: `${data?.employee?.first_name || ""} ${data?.employee?.last_name || ""}`,
            col3: <Text strong>PF UAN </Text>,
            col4: data?.employee?.uan_number || "-",
        },
        {
            key: 2,
            col1: <Text strong>Employee ID </Text>,
            col2: data?.employee?.emp_code || "-",
            col3: <Text strong>ESIC IP No. </Text>,
            col4: data?.esci_no || "-",
        },
        {
            key: 3,
            col1: <Text strong>Designation </Text>,
            col2: data?.employee?.designation_name || "-",
            col3: <Text strong>PAN No. </Text>,
            col4: data?.employee?.pan_card_number || "-",
        },
        {
            key: 4,
            col1: <Text strong>Department </Text>,
            col2: fs.department || "-",
            col3: <Text strong>Aadhar Number </Text>,
            col4: fs.aadhar_no || "-",
        },
        {
            key: 5,
            col1: <Text strong>Date of Joining (D.O.J.) </Text>,
            col2: data?.employee?.date_of_joining || "-",
            col3: <Text strong>No. of working days </Text>,
            col4: fs.month_days ?? "-",
        },
        {
            key: 6,
            col1: <Text strong>Bank Name </Text>,
            col2: fs.bank_name || "-",
            col3: <Text strong>No. of days payable </Text>,
            col4: fs.days_payable ?? "-",
        },
        {
            key: 7,
            col1: <Text strong>Bank Account Number </Text>,
            col2: data?.employee?.bank_account_number || fs.account_no || "-",
            col3: <Text strong>Loss of Pay (LOP) </Text>,
            col4: fs.lop_days ?? "-",
        },
        {
            key: 8,
            col1: <Text strong>IFSC Code </Text>,
            col2: fs.ifsc_code || "-",
            col3: <Text strong>Bank Branch </Text>,
            col4: fs.bank_branch || "-",
        },
    ];

    return (
        <>
            <style>
                {`
            .total-row td {
                font-weight: bold !important;
            }
        `}
            </style>
            <Row gutter={[10, 10]} style={{ marginBottom: "20px" }} className="no-print" align="middle">
                {onBack && (
                    <Col>
                        <Button onClick={onBack}>← Back</Button>
                    </Col>
                )}

                <Col>
                    <Select value={month} onChange={setMonth} style={{ width: 160 }}>
                        {monthOptions.map((m, index) => (
                            <Option key={index + 1} value={index + 1}>
                                {m}
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col>
                    <Select value={year} onChange={setYear} style={{ width: 120 }}>
                        {yearOptions.map((y) => (
                            <Option key={y} value={y}>
                                {y}
                            </Option>
                        ))}
                    </Select>
                </Col>

                <Col>
                    <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={handleDownload}
                        style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                        disabled={!data || loading || downloading}
                    >
                        {downloading ? "Generating..." : "Download Pay Slip"}
                    </Button>
                </Col>
            </Row>

            {/* LOADING */}
            {loading && !data && <Spin tip="Loading Payslip..." />}

            {/* NO DATA */}
            {!loading && !data && (
                <Text style={{ fontSize: 16, color: "red" }}>
                    No payslip found for the selected month/year.
                </Text>
            )}

            {/* PAYSLIP TEMPLATE — mirrors BFMS_BULKPAYSLIP.xlsm "PAYSLIP" sheet exactly */}
            {!data ? null : (
                <div ref={slipRef} style={{ background: "#f0f2f5", padding: "20px 0" }}>
                    <Card
                        ref={cardRef}
                        bordered={false}
                        style={{
                            width: "210mm",   // Exact A4 Width
                            minHeight: "297mm", // Exact A4 Height
                            margin: "0 auto",
                            padding: "20mm",   // Standard margin
                            backgroundColor: "#fff",
                            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
                            fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
                        }}
                        bodyStyle={{ padding: 0 }}
                    >
                        {/* HEADER */}
                        <Table
                            columns={[
                                {
                                    dataIndex: "logo",
                                    key: "logo",
                                    width: 100,
                                    render: () => (
                                        <Image
                                            src={BashyamLogo}
                                            alt="Baashyaam Logo"
                                            width={150}
                                            preview={false}
                                        />
                                    ),
                                },
                                {
                                    dataIndex: "info",
                                    key: "info",
                                    align: "center",
                                    render: () => (
                                        <>
                                            {/* A2: bold, 13.5pt, centered. Default text matches the
                                                sheet's literal company name when org name is absent. */}
                                            <Text strong style={{ fontSize: 14 }}>
                                                {data?.employee?.organisation_name?.toUpperCase() ||
                                                    "BAASHYAAM FMS PVT LTD"}
                                            </Text>
                                            <br />
                                            {/* A3: address line, regular weight, centered */}
                                            <Text style={{ fontSize: "8px" }}>No.87, GN CHETTY ROAD, CHENNAI 600017</Text>
                                        </>
                                    ),
                                },
                                {
                                    dataIndex: "empImg",
                                    key: "empImg",
                                    width: 100,
                                    render: () => (
                                        <>
                                            {
                                                (data?.employee?.photo_base64 || authData?.employee?.photo) ? (
                                                    <Image
                                                        src={data?.employee?.photo_base64 || `${authData?.employee?.photo}`}
                                                        alt="Employee Image"
                                                        width={100}
                                                        height={90}
                                                        preview={false}
                                                    />
                                                ) : (
                                                    <>
                                                    </>
                                                )
                                            }
                                        </>

                                    ),
                                },
                            ]}
                            dataSource={[{ key: "1" }]}
                            pagination={false}
                            bordered={false}
                            showHeader={false}
                        />

                        {/* A4: "PAY SLIP FOR THE MONTH OF" band — light grey fill, centered */}
                        <div
                            style={{
                                backgroundColor: HEADER_BAND_COLOR,
                                textAlign: "center",
                                padding: "6px 0",
                                margin: "8px 0",
                                color: 'white'
                            }}
                        >
                            <Text strong style={{ color: 'white' }}>
                                PAY SLIP FOR THE MONTH OF {monthOptions[month - 1].toUpperCase()} {year}
                            </Text>
                        </div>

                        {/* EMPLOYEE DETAILS — exactly matching PAYSLIP sheet rows A5:E12 */}
                        <Table
                            bordered
                            pagination={false}
                            showHeader={false}
                            style={{ marginTop: "10px", marginBottom: "10px" }}
                            dataSource={employeeDetailRows}
                            columns={[
                                { dataIndex: "col1", key: "col1" },
                                { dataIndex: "col2", key: "col2" },
                                { dataIndex: "col3", key: "col3" },
                                { dataIndex: "col4", key: "col4" },
                            ]}
                        />

                        {/* EARNINGS & DEDUCTIONS — matching PAYSLIP sheet rows A13:E24 */}
                        <Row gutter={16}>
                            <Col span={14}>
                                <div style={sectionHeaderStyle}>Earnings in INR</div>

                                <Table
                                    bordered
                                    pagination={false}
                                    size="small"
                                    dataSource={earningsRows}
                                    rowClassName={(record) => record.className || ""}
                                    columns={[
                                        { title: "Particulars", dataIndex: "name" },
                                        {
                                            title: "Fixed Earnings",
                                            dataIndex: "fixed",
                                            align: "right",
                                            render: (v) => (v === null ? "\u00A0" : fmt(v)),
                                        },
                                        {
                                            title: "Actual Earnings",
                                            dataIndex: "actual",
                                            align: "right",
                                            render: (v) => (v === null ? "\u00A0" : fmt(v)),
                                        },
                                    ]}
                                    components={{
                                        header: {
                                            cell: (props) => (
                                                <th {...props} style={sectionHeaderStyle}>
                                                    {props.children}
                                                </th>
                                            ),
                                        },
                                    }}
                                />
                            </Col>

                            <Col span={10}>
                                <div style={sectionHeaderStyle}>Deductions in INR</div>

                                <Table
                                    bordered
                                    pagination={false}
                                    size="small"
                                    dataSource={deductionsRows}
                                    rowClassName={(record) => record.className || ""}
                                    columns={[
                                        { title: "Particulars", dataIndex: "name" },
                                        {
                                            title: "Amount (₹)",
                                            dataIndex: "amount",
                                            align: "right",
                                            render: (v) => fmt(v),
                                        },
                                    ]}
                                    components={{
                                        header: {
                                            cell: (props) => (
                                                <th {...props} style={sectionHeaderStyle}>
                                                    {props.children}
                                                </th>
                                            ),
                                        },
                                    }}
                                />
                            </Col>
                        </Row>

                        <Divider />

                        {/* NET PAY — A25 (label + words, merged) and D25 (amount, merged)
                            sit side-by-side in a single band in the sheet. */}
                        <Row align="middle">
                            <Col span={16}>
                                <Title level={5} style={{ color: "#0e497b", margin: 0 }}>
                                    NET PAY {fs.net_pay_in_words ? `(Rupees ${fs.net_pay_in_words})` : ""}
                                </Title>
                            </Col>

                            <Col span={8} style={{ textAlign: "right" }}>
                                <Title level={5} style={{ color: "#0e497b", margin: 0 }}>
                                    ₹{fmt(fs.net_pay)}
                                </Title>
                            </Col>
                        </Row>

                        <Divider />

                        {/* A27: PT note — present in sheet, was missing from JSX */}
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Note : Professional Tax deductions will be made once every six months Based on the slab
                        </Text>

                        <Table
                            bordered
                            pagination={false}
                            dataSource={[
                                {
                                    key: 1,
                                    leave: "Casual Leave",
                                    opening: data?.leave_balance?.[0]?.opening ?? 0,
                                    availed: data?.leave_balance?.[0]?.availed ?? 0,
                                    balance: data?.leave_balance?.[0]?.balance ?? 0,
                                },
                                {
                                    key: 2,
                                    leave: "Sick Leave",
                                    opening: data?.leave_balance?.[1]?.opening ?? 0,
                                    availed: data?.leave_balance?.[1]?.availed ?? 0,
                                    balance: data?.leave_balance?.[1]?.balance ?? 0,
                                },
                            ]}
                            columns={[
                                { title: "Leave Type", dataIndex: "leave", align: "center" },
                                { title: "Opening", dataIndex: "opening", align: "center" },
                                { title: "Availed", dataIndex: "availed", align: "center" },
                                { title: "Balance", dataIndex: "balance", align: "center" },
                            ]}
                            components={{
                                header: {
                                    cell: (props) => (
                                        <th {...props} style={sectionHeaderStyle}>
                                            {props.children}
                                        </th>
                                    ),
                                },
                            }}
                            style={{ marginTop: "10px", marginBottom: "10px" }}
                        />

                        <Divider />

                        <center>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                This is system generated pay slip hence does not require Signature.
                            </Text>
                        </center>
                    </Card>
                </div>
            )}
        </>
    );
};

export default PaySlip;